import { Item as DictionaryItem } from './generic';

/**
 * Soft facet inferred by AI. Used for grouping/display only — it is never a
 * structural constraint. The one hard rule it carries: the vector resolver
 * never merges competences across different kinds.
 */
export enum Kind {
  HARD = 'HARD',
  SOFT = 'SOFT',
  SPECIALITY = 'SPECIALITY',
  OTHER = 'OTHER',
}

/**
 * PENDING — freshly created from free text, not embedded yet.
 * ACTIVE  — embedded and resolved; the canonical form.
 * MERGED  — folded into another competence; `mergedIntoId` points at the winner.
 */
export enum Status {
  PENDING = 'PENDING',
  ACTIVE = 'ACTIVE',
  MERGED = 'MERGED',
}

/**
 * Provenance shown on the candidate card.
 * CV     — extracted automatically from a file (lives in `files_competence`).
 * MANUAL — added by a recruiter.
 * IMPORT — carried over from the legacy skills/specialities dictionaries.
 */
export enum Source {
  CV = 'CV',
  MANUAL = 'MANUAL',
  IMPORT = 'IMPORT',
}

/** Optional substring match against the canonical label or any translation. */
export interface ListQuery {
  search?: string;
}

export interface Item extends DictionaryItem {
  slug: string;
  kind: Kind;
}

export interface Details extends Item {
  status: Status;
  mergedIntoId: number | null;
}

/**
 * A competence as attached to a profile or a vacancy. `source` and `fileId`
 * come from the unified read view: `fileId` is set only for CV-derived rows.
 */
export interface Reference extends Item {
  source: Source;
  fileId: number | null;
  /**
   * Held by so many profiles that it says little about this one — `Excel`,
   * `MS Office`, `SQL`. Clients order these LAST within their kind so a
   * card spends its width on what distinguishes the candidate.
   *
   * Strictly an ordering hint, never a reason to hide anything: a candidate
   * whose whole set is generic still has all of it shown. Derived server-side
   * from `competence.profile_count` against a configured threshold, because
   * the cutoff depends on corpus size and does not belong in a client.
   */
  isGeneric: boolean;
}

export interface ResolveItem {
  label: string;
  kind?: Kind;
}

export interface ResolvePayload {
  items: ResolveItem[];
}

/**
 * Query for the admin grid (`GET /competence/admin`). Unlike the public
 * dictionary read it sees every status and is never served from cache.
 */
export interface AdminQuery {
  /** Matches the canonical label, the slug, or any translation (substring). */
  search?: string;
  status?: Status[];
  kind?: Kind[];
  page?: number;
  size?: number;
  sortBy?: 'id' | 'label' | 'profileCount' | 'status' | 'kind';
  sortOrder?: 'ASC' | 'DESC';
}

/**
 * Row of the admin grid. `name` (inherited) carries the CANONICAL label —
 * not the locale-resolved fallback the public dictionary read returns.
 */
export interface AdminItem extends Details {
  profileCount: number;
  /** Canonical label of the merge target the row points at, if any. */
  mergedIntoName: string | null;
  enName: string | null;
  ruName: string | null;
  ukName: string | null;
}

/** Row of the merge-review bucket (`GET /competence/merge-candidates`). */
export interface MergeCandidate extends Details {
  mergedIntoName: string | null;
}

export interface CreatePayload {
  label: string;
  kind: Kind;
  enName?: string;
  ruName?: string;
  ukName?: string;
}

export interface UpdatePayload {
  label: string;
  kind: Kind;
  /** undefined — leave the translation untouched; null or '' — delete it; value — upsert. */
  enName?: string | null;
  ruName?: string | null;
  ukName?: string | null;
  /** The only transition the dictionary accepts is PENDING → ACTIVE. */
  status?: Status;
}

export interface MergePayload {
  sourceId: number;
  targetId: number;
}

/** Rejects the merge suggestion the source row carries, so it is not re-proposed. */
export interface RejectMergePayload {
  sourceId: number;
}
